import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

// Interface for data to be copied from Active Metrics to Order Panel
export interface OrderCopyData {
  instrument: 'NIFTY' | 'BANKNIFTY' | 'SENSEX' | 'FINNIFTY' | 'BANKEX';
  customStrike: string;
  optionType: 'CE' | 'PE';
  side: 'Buy' | 'Sell';
  quantity: string;
  price: string;
  entryLogic: 'Spot' | 'Instant';
  logicCondition: '>' | '<';
  logicSpotPrice: string;
  slEnabled: boolean;
  slPrice: string;
  tgEnabled: boolean;
  tgPrice: string;
  hedge: boolean;
  originalSymbol: string;
  originalUid: number;
}

interface OrderPanelContextType {
  copyData: OrderCopyData | null;
  copyToOrderPanel: (data: OrderCopyData) => void;
  clearCopyData: () => void;
}

const OrderPanelContext = createContext<OrderPanelContextType | undefined>(undefined);

export const useOrderPanelContext = () => {
  const context = useContext(OrderPanelContext);
  if (context === undefined) {
    throw new Error('useOrderPanelContext must be used within an OrderPanelProvider');
  }
  return context;
};

interface OrderPanelProviderProps {
  children: ReactNode;
}

export const OrderPanelProvider: React.FC<OrderPanelProviderProps> = ({ children }) => {
  const [copyData, setCopyData] = useState<OrderCopyData | null>(null);

  const copyToOrderPanel = useCallback((data: OrderCopyData) => {
    setCopyData(data);
  }, []);

  const clearCopyData = useCallback(() => {
    setCopyData(null);
  }, []);

  const contextValue = useMemo(() => ({
    copyData,
    copyToOrderPanel,
    clearCopyData
  }), [copyData, copyToOrderPanel, clearCopyData]);

  return (
    <OrderPanelContext.Provider value={contextValue}>
      {children}
    </OrderPanelContext.Provider>
  );
};